// The reader's own line, after v1's exploration, as a stack with a cursor.
// The stack holds the moves played from the game ply the line branches at,
// each with the position it led to; the cursor is how many of them are
// applied. Going back moves the cursor down and keeps the moves above it, so
// going forward again, or replaying the same move, re-enters the line;
// playing a different move from below the top drops everything above the
// cursor first. A `Line` is immutable: every change gives a new one, so the
// review's state snapshots stay honest for anyone holding an older one.
//
// `canForward` and `forward()` exist so that stepping forward inside a line
// can be enabled later in one place; the step controls do not use them yet
// (stepping forward stays disabled while exploring, as the user asked).
import { type Fen, type Move } from '@chess/api-model';
import type { ReviewExploration } from './index.ts';

export interface LineEntry {
  move: Move;
  /** The position after `move`. */
  position: Fen;
}

export class Line implements ReviewExploration {
  /** The game ply the line branches from. */
  readonly fromPly: number;
  /** The game's position at `fromPly`. */
  readonly start: Fen;
  /** Every move played in the line, applied or not. */
  readonly stack: readonly LineEntry[];
  /** How many entries of the stack are applied. */
  readonly cursor: number;

  constructor(fromPly: number, start: Fen, stack: readonly LineEntry[] = [], cursor = stack.length) {
    this.fromPly = fromPly;
    this.start = start;
    this.stack = stack;
    this.cursor = Math.max(0, Math.min(cursor, stack.length));
  }

  /** No move of the line is applied: the board is on the game at `fromPly`. */
  get empty(): boolean {
    return this.cursor === 0;
  }

  /** The applied moves, oldest first. */
  get moves(): Move[] {
    return this.stack.slice(0, this.cursor).map((e) => e.move);
  }

  /** `positions[0]` is the game's position at `fromPly`; `positions[i]` is after `moves[i - 1]`. */
  get positions(): Fen[] {
    return [this.start, ...this.stack.slice(0, this.cursor).map((e) => e.position)];
  }

  /** The position under the cursor. */
  get fen(): Fen {
    return this.cursor === 0 ? this.start : this.stack[this.cursor - 1].position;
  }

  /** The applied move under the cursor, if any. */
  get lastMove(): Move | undefined {
    return this.cursor === 0 ? undefined : this.stack[this.cursor - 1].move;
  }

  /** The ply along the game's path the cursor has reached. */
  get headPly(): number {
    return this.fromPly + this.cursor;
  }

  get length(): number {
    return this.cursor;
  }

  get canBack(): boolean {
    return this.cursor > 0;
  }

  get canForward(): boolean {
    return this.cursor < this.stack.length;
  }

  /**
   * Play a move from the cursor. The move already at the cursor re-enters the
   * line by advancing; any other move drops the entries above the cursor and
   * pushes the new one.
   */
  play(move: Move, position: Fen): Line {
    const next = this.stack[this.cursor];
    if (next && next.move.uci === move.uci) return new Line(this.fromPly, this.start, this.stack, this.cursor + 1);
    return new Line(this.fromPly, this.start, [...this.stack.slice(0, this.cursor), { move, position }]);
  }

  /** The cursor one entry down; at 0 the line is empty and the board is on the game. */
  back(): Line {
    return new Line(this.fromPly, this.start, this.stack, this.cursor - 1);
  }

  /** The cursor one entry up, while below the top. */
  forward(): Line {
    return new Line(this.fromPly, this.start, this.stack, this.cursor + 1);
  }

  /** The position at a ply along the path, when the ply is inside the applied line. */
  positionAt(ply: number): Fen | undefined {
    if (ply < this.fromPly || ply > this.headPly) return undefined;
    return ply === this.fromPly ? this.start : this.stack[ply - this.fromPly - 1].position;
  }

  /** The move that led to a ply along the path, when it is one of the applied line's. */
  moveAt(ply: number): Move | undefined {
    if (ply <= this.fromPly || ply > this.headPly) return undefined;
    return this.stack[ply - this.fromPly - 1].move;
  }

  /** Plain data, for the review state. */
  toExploration(): ReviewExploration {
    return { fromPly: this.fromPly, moves: this.moves, positions: this.positions, fen: this.fen };
  }
}
